import React, { useState } from 'react';
import { Moon, ArrowRight, Lock, ShieldAlert } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { track } from '../lib/analytics';

const SPUN_FUN_URL = 'https://viral-edge-hub.preview.emergentagent.com/';
const CONSENT_KEY = 'mindesign_afterdark_consent';

/**
 * MINDESIGN PORTAL TO THE AFTERDARK
 *
 * A distinct neon "after dark" section on the gunmetal main site that gates
 * access to the Spun Fun adult community via an 18+ confirmation modal.
 * Consent is remembered for the session so repeat visitors don't get re-prompted.
 */
export const AfterDarkPortal = () => {
  const [open, setOpen] = useState(false);

  // Open in a new tab when allowed; fall back to same-tab if the popup is blocked
  // (Safari/Brave/strict Firefox can drop window.open after a React setState because
  // the user-gesture context is lost).
  const openSpunFun = () => {
    const win = window.open(SPUN_FUN_URL, '_blank', 'noopener,noreferrer');
    if (!win || win.closed || typeof win.closed === 'undefined') {
      // Popup was blocked — navigate this tab instead.
      window.location.href = SPUN_FUN_URL;
    }
  };

  const handleEnter = () => {
    // Skip the gate if already confirmed in this session
    if (typeof window !== 'undefined' && sessionStorage.getItem(CONSENT_KEY) === 'yes') {
      openSpunFun();
      return;
    }
    setOpen(true);
  };

  const confirmAndEnter = () => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(CONSENT_KEY, 'yes');
    }
    track('afterdark_entered', { source: 'portal_modal' });
    // Open BEFORE closing the modal so we stay inside the user-gesture stack frame
    // (preserves popup-permission in Safari/Brave).
    openSpunFun();
    setOpen(false);
  };

  const exitGate = () => setOpen(false);

  return (
    <section
      id="afterdark"
      className="relative py-24 px-6 overflow-hidden"
      data-testid="afterdark-portal-section"
    >
      {/* Layered after-dark backdrop — distinct from the main gunmetal aesthetic */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0a0014] via-[#1a0033] to-[#000]"></div>
      <div
        className="absolute inset-0 opacity-50 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 20% 50%, rgba(236,72,153,0.25) 0%, transparent 50%), radial-gradient(circle at 80% 30%, rgba(168,85,247,0.25) 0%, transparent 50%), radial-gradient(circle at 50% 80%, rgba(59,130,246,0.18) 0%, transparent 50%)',
        }}
      ></div>
      {/* Scanline texture */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none mix-blend-screen"
        style={{
          backgroundImage:
            'repeating-linear-gradient(0deg, rgba(255,255,255,0.5) 0, rgba(255,255,255,0.5) 1px, transparent 1px, transparent 3px)',
        }}
      ></div>

      <div className="relative max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          {/* Copy */}
          <div className="order-2 md:order-1">
            <div className="inline-flex items-center gap-2 mb-5">
              <span className="text-[10px] tracking-[0.4em] uppercase text-pink-400/90 font-bold flex items-center gap-2">
                <Moon size={14} className="text-pink-400" />
                MINDESIGN PORTAL · 18+ ONLY
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-5 leading-[0.95]">
              <span className="text-white">PORTAL TO THE</span>
              <br />
              <span
                className="bg-clip-text text-transparent bg-gradient-to-r from-pink-400 via-fuchsia-400 to-purple-400"
                style={{
                  textShadow: '0 0 60px rgba(236,72,153,0.4)',
                }}
              >
                AFTER DARK
              </span>
            </h2>

            <p className="text-pink-100/70 text-lg leading-relaxed mb-3 italic">
              Where the night comes alive.
            </p>
            <p className="text-pink-100/60 text-base leading-relaxed mb-8 max-w-md">
              The MINDESIGN ENTERPRISES gateway to <strong className="text-pink-300">SPUN FUN &amp; FUN SPUN</strong> —
              an exclusive 18+ adult community for the after-dark crowd.
              Real connections, raw conversations, and the kind of vibe the
              daytime web won't allow.
            </p>

            <div className="flex flex-wrap gap-3">
              <Button
                onClick={handleEnter}
                className="group relative bg-gradient-to-r from-pink-500 to-fuchsia-600 hover:from-pink-400 hover:to-fuchsia-500 text-white font-black tracking-[0.2em] uppercase px-8 py-6 text-sm shadow-[0_0_40px_rgba(236,72,153,0.5)] hover:shadow-[0_0_60px_rgba(236,72,153,0.8)] transition-all duration-300"
                data-testid="afterdark-enter-btn"
              >
                <Lock size={16} className="mr-2 opacity-80" />
                Enter the Portal
                <ArrowRight
                  size={16}
                  className="ml-2 group-hover:translate-x-1 transition-transform"
                />
              </Button>
            </div>

            <div className="mt-6 flex items-center gap-2 text-pink-200/40 text-xs">
              <ShieldAlert size={12} />
              <span>You must confirm you're 18 or older to enter.</span>
            </div>
          </div>

          {/* Glowing portal card */}
          <div className="order-1 md:order-2 flex justify-center">
            <button
              onClick={handleEnter}
              className="group relative w-72 h-72 sm:w-80 sm:h-80"
              aria-label="Open After Dark portal"
              data-testid="afterdark-portal-glow"
            >
              {/* Outer glow rings */}
              <span className="absolute inset-0 rounded-full bg-gradient-to-br from-pink-500/30 via-fuchsia-500/20 to-purple-700/30 blur-3xl group-hover:blur-2xl transition-all duration-500 animate-pulse"></span>
              <span className="absolute inset-2 rounded-full border border-pink-400/30 group-hover:border-pink-400/70 transition-colors"></span>
              <span className="absolute inset-6 rounded-full border border-fuchsia-400/20 group-hover:border-fuchsia-400/60 transition-colors"></span>

              {/* Portal core */}
              <span className="absolute inset-12 rounded-full bg-gradient-to-br from-pink-600 via-fuchsia-700 to-purple-900 shadow-[inset_0_0_40px_rgba(0,0,0,0.6),0_0_80px_rgba(236,72,153,0.4)] flex items-center justify-center overflow-hidden">
                <span className="text-center text-white font-black tracking-[0.25em] uppercase text-sm leading-tight px-4">
                  Spun Fun
                  <br />
                  <span className="text-pink-200 text-[10px] tracking-[0.4em]">&amp;</span>
                  <br />
                  Fun Spun
                  <br />
                  <span className="text-pink-300 text-xs tracking-[0.3em] mt-2 inline-block">After Dark</span>
                </span>
                {/* Rotating sheen */}
                <span className="absolute inset-0 rounded-full bg-gradient-to-tr from-transparent via-white/10 to-transparent rotate-12"></span>
              </span>

              {/* Hover hint */}
              <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.3em] uppercase text-pink-300/70 group-hover:text-pink-300 transition-colors whitespace-nowrap">
                Tap to enter · 18+
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* 18+ Age Gate Modal */}
      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : exitGate())}>
        <DialogContent
          className="bg-gradient-to-br from-[#0a0014] to-[#1a0033] border border-pink-500/40 text-pink-50 max-w-md"
          data-testid="afterdark-age-gate"
        >
          <DialogHeader>
            <DialogTitle className="text-2xl font-black tracking-widest uppercase bg-clip-text text-transparent bg-gradient-to-r from-pink-400 to-fuchsia-400 flex items-center gap-2">
              <ShieldAlert size={22} className="text-pink-400" />
              Age Verification Required
            </DialogTitle>
            <DialogDescription className="text-pink-100/70 pt-2">
              You're about to leave MINDESIGN ENTERPRISES and enter
              <strong className="text-pink-300"> SPUN FUN &amp; FUN SPUN — AFTER DARK</strong>,
              an adult community for users 18 years or older.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-md border border-pink-500/30 bg-pink-500/5 p-4 my-2 text-sm text-pink-100/80 leading-relaxed">
            By entering, you confirm that you are of legal age in your
            jurisdiction and that viewing adult content is legal where you live.
          </div>

          <DialogFooter className="gap-2 sm:gap-2 sm:flex-row-reverse">
            <Button
              onClick={confirmAndEnter}
              className="bg-gradient-to-r from-pink-500 to-fuchsia-600 hover:from-pink-400 hover:to-fuchsia-500 text-white font-black tracking-widest uppercase shadow-[0_0_30px_rgba(236,72,153,0.5)]"
              data-testid="afterdark-confirm-btn"
            >
              I am 18+ — Enter
            </Button>
            <Button
              onClick={exitGate}
              variant="ghost"
              className="text-pink-200/70 hover:text-pink-100 hover:bg-pink-500/10 border border-pink-500/20"
              data-testid="afterdark-exit-btn"
            >
              I am under 18 — Exit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default AfterDarkPortal;
